// hub → Studio → Engines: which engine each step uses by default (photos per
// department), whether each engine's key is on this server (never the key
// itself), what a run costs, and which engine has won the comparisons so far.
import { useEffect, useState } from "react";
import { Check, Loader2, Plus, X } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { VIEW_LABEL, duration, hex, money, type EnginesInfo, type EngineSettings, type PhotoDefault } from "./workbench";

export function EnginesTab() {
  const [info, setInfo] = useState<EnginesInfo | null>(null);
  const [draft, setDraft] = useState<EngineSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<EnginesInfo>("/studio/workbench/engines").then((x) => { setInfo(x); setDraft(x.settings); }).catch((e) => setError(apiErrorMessage(e, "Could not load the engines.")));
  }, []);
  if (error) return <p className="mt-bay text-label text-risk">{error}</p>;
  if (!info || !draft) return <p className="mt-bay flex items-center gap-2 text-label text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading the engines…</p>;
  const dirty = JSON.stringify(draft) !== JSON.stringify(info.settings);
  const deptName = (l: string) => info.departments.find((d) => d.letter === l)?.name ?? l;
  const unused = info.departments.filter((d) => !draft.photos.byDepartment[d.letter]);
  const setDept = (l: string, v: PhotoDefault | null) => setDraft((s) => {
    const by = { ...s!.photos.byDepartment };
    if (v) by[l] = v; else delete by[l];
    return { ...s!, photos: { ...s!.photos, byDepartment: by } };
  });
  const save = async () => {
    setBusy(true); setSaved(null); setError(null);
    try {
      const r = await api<{ settings: EngineSettings }>("/studio/workbench/settings", { method: "PUT", body: JSON.stringify({ settings: draft }) });
      setInfo({ ...info, settings: r.settings }); setDraft(r.settings); setSaved("Saved. New runs use these engines.");
    } catch (e) { setError(apiErrorMessage(e, "Could not save.")); } finally { setBusy(false); }
  };
  const replicate = info.keys.find((k) => k.name === "REPLICATE_API_TOKEN");

  return (
    <div className="mt-bay flex flex-col gap-gutter">
      <section className="flex flex-col gap-4 rounded-lg border border-rule bg-white p-5">
        <header>
          <h2 className="text-label font-bold">Default engines</h2>
          <p className="text-meta text-ink-muted">What “Run all” and batches from Products use. You can always pick another engine on a product.</p>
        </header>
        <div className="flex flex-col divide-y divide-rule rounded-lg border border-rule">
          {Object.entries(draft.photos.byDepartment).map(([l, v]) => (
            <PhotoRow key={l} title={`Photos — ${deptName(l)}`} value={v} info={info} onChange={(x) => setDept(l, x)} onRemove={() => setDept(l, null)} />
          ))}
          <PhotoRow title={Object.keys(draft.photos.byDepartment).length ? "Photos — everything else" : "Photos"} value={draft.photos.fallback} info={info} onChange={(x) => setDraft({ ...draft, photos: { ...draft.photos, fallback: x } })} />
          <div className="flex flex-wrap items-center gap-3 p-4">
            <span className="min-w-[200px] text-label font-semibold">Descriptions</span>
            <select value={draft.description.engine} onChange={(e) => setDraft({ ...draft, description: { engine: e.target.value } })} className="h-9 rounded-md border border-rule bg-white px-2 text-meta" aria-label="Description model">
              {info.engines.text.map((c) => <option key={c.id} value={c.id}>{c.label} · ~{money(c.costUsd)}{c.ready ? "" : " — add a key"}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {unused.length > 0 && (
            <label className="flex items-center gap-2 text-meta">
              <Plus className="h-3.5 w-3.5" aria-hidden />
              <select value="" onChange={(e) => e.target.value && setDept(e.target.value, { ...draft.photos.fallback })} className="h-8 rounded-md border border-rule bg-white px-2 text-meta" aria-label="Add a department rule">
                <option value="">Set photos for a department…</option>
                {unused.map((d) => <option key={d.letter} value={d.letter}>{d.name}</option>)}
              </select>
            </label>
          )}
          <span className="ml-auto text-meta text-ink-muted">{saved ?? (info.updatedAt ? `Last changed ${new Date(info.updatedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : "Using the built-in defaults")}</span>
          <Button size="sm" disabled={!dirty || busy} onClick={() => void save()}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Check className="h-3.5 w-3.5" aria-hidden />} Save</Button>
          {dirty && <Button size="sm" variant="ghost" onClick={() => setDraft(info.settings)}>Undo</Button>}
        </div>
      </section>

      <section className="flex flex-col gap-3 rounded-lg border border-rule bg-white p-5">
        <h2 className="text-label font-bold">Engines</h2>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {[...info.engines.photo, ...info.engines.text].map((c) => (
            <article key={c.id} className="flex flex-col gap-1 rounded-lg border border-rule p-4">
              <span className="flex items-start gap-2">
                <span className="flex-1 text-label font-semibold">{c.label}</span>
                <span className={cn("rounded px-1.5 py-0.5 text-[12px] font-semibold", c.ready ? "bg-ok-bg text-ok" : "bg-warn-bg text-warn")}>{c.ready ? "Ready" : "Needs a key"}</span>
              </span>
              <span className="text-meta text-ink-muted">{c.blurb}</span>
              <span className="mt-1 text-meta">About {money(c.costUsd)} and {duration(c.seconds)} a {c.kind ? "photo" : "description"}.</span>
              {!c.ready && <span className="text-meta text-warn">Add: {c.missing.join(", ")}</span>}
            </article>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3 rounded-lg border border-rule bg-white p-5">
        <h2 className="text-label font-bold">API keys on this server</h2>
        <p className="text-meta text-ink-muted">Only whether each key is there — never the key itself.</p>
        <ul className="divide-y divide-rule rounded-lg border border-rule">
          {info.keys.map((k) => (
            <li key={k.name} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <span className={cn("inline-flex h-5 w-5 items-center justify-center rounded-full", k.configured ? "bg-ok-bg text-ok" : "bg-warn-bg text-warn")} aria-hidden>{k.configured ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}</span>
              <span className="min-w-[180px] text-label font-semibold">{k.label}</span>
              <span className="text-meta">{k.configured ? "Configured" : "Missing"}</span>
              <span className="flex-1 text-meta text-ink-muted">{k.usedFor}{k.configured ? "" : ` · ${k.howToAdd}`}</span>
            </li>
          ))}
        </ul>
        {replicate?.configured && (
          <p className="text-meta text-ink-muted">Replicate allows only 6 photos a minute while the account has under $5 of credit. If a run says the provider is throttling, add credit at replicate.com, then run again.</p>
        )}
      </section>

      <section className="flex flex-col gap-3 rounded-lg border border-rule bg-white p-5">
        <h2 className="text-label font-bold">What has won so far</h2>
        {info.scoreboard.length === 0 ? (
          <p className="text-meta text-ink-muted">No comparisons yet. Use “Compare engines” on a product; every pick is counted here by department.</p>
        ) : (
          <table className="w-full text-left text-meta">
            <thead className="text-ink-muted"><tr><th className="py-1 font-semibold">Step</th><th className="py-1 font-semibold">Department</th><th className="py-1 font-semibold">Engine</th><th className="py-1 text-right font-semibold">Wins</th></tr></thead>
            <tbody>
              {info.scoreboard.map((r, i) => (
                <tr key={i} className="border-t border-rule">
                  <td className="py-1.5">{r.step === "photo" ? "Photos" : "Descriptions"}</td>
                  <td className="py-1.5">{r.department ?? "—"}</td>
                  <td className="py-1.5">{[...info.engines.photo, ...info.engines.text].find((e) => e.id === r.engine)?.label ?? r.engine}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.wins}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function PhotoRow({ title, value, info, onChange, onRemove }: { title: string; value: PhotoDefault; info: EnginesInfo; onChange: (v: PhotoDefault) => void; onRemove?: () => void }) {
  const toggle = (v: string) => {
    const has = value.views.includes(v);
    const next = has ? value.views.filter((x) => x !== v) : [...value.views, v];
    if (next.length) onChange({ ...value, views: next });
  };
  const bd = info.backdrops.find((b) => b.name === value.backdrop);
  return (
    <div className="flex flex-wrap items-center gap-3 p-4">
      <span className="min-w-[200px] text-label font-semibold">{title}</span>
      <select value={value.engine} onChange={(e) => onChange({ ...value, engine: e.target.value })} className="h-9 rounded-md border border-rule bg-white px-2 text-meta" aria-label={`${title}: engine`}>
        {info.engines.photo.map((c) => <option key={c.id} value={c.id}>{c.label}{c.ready ? "" : " — add a key"}</option>)}
      </select>
      <span className="flex items-center gap-2">
        <span className="h-5 w-5 rounded border border-rule" style={{ background: hex(bd?.colour ?? [245, 245, 242]) }} aria-hidden />
        <select value={value.backdrop} onChange={(e) => onChange({ ...value, backdrop: e.target.value })} className="h-9 rounded-md border border-rule bg-white px-2 text-meta" aria-label={`${title}: backdrop`}>
          {info.backdrops.map((b) => <option key={b.name} value={b.name}>{b.name} — {b.label}</option>)}
        </select>
      </span>
      <span className="flex flex-wrap gap-1" role="group" aria-label={`${title}: views`}>
        {info.views.map((v) => (
          <button key={v} type="button" aria-pressed={value.views.includes(v)} onClick={() => toggle(v)}
            className={cn("rounded-full border px-2.5 py-1 text-[12px]", value.views.includes(v) ? "border-ink bg-ink text-white" : "border-rule bg-white hover:bg-paper")}>
            {VIEW_LABEL[v] ?? v}
          </button>
        ))}
      </span>
      {onRemove && <Button size="sm" variant="ghost" className="ml-auto" onClick={onRemove}><X className="h-3.5 w-3.5" aria-hidden /> Remove</Button>}
    </div>
  );
}
